import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import DashboardHeader from "../components/DashboardHeader";

const setApi = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/sets`;

export default function SetLibraryPage({ user, onLogout }) {
  const [sets, setSets] = useState([]);
  const [facets, setFacets] = useState({ eras: [], product_types: [] });
  const [search, setSearch] = useState("");
  const [era, setEra] = useState("");
  const [productType, setProductType] = useState("");
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);

  useEffect(() => {
    axios
      .get(`${setApi}/facets`, {
        params: { game: "Pokémon" },
        withCredentials: true,
      })
      .then((response) => setFacets(response.data))
      .catch(() => toast.error("Cardfolio couldn’t load Set Library filters."));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    setLoading(true);
    axios
      .get(setApi, {
        params: {
          game: "Pokémon",
          search,
          era: era || undefined,
          product_type: productType || undefined,
          limit: 200,
        },
        withCredentials: true,
        signal: controller.signal,
      })
      .then((response) => {
        if (requestId === requestSequence.current) {
          setSets(response.data.sets);
        }
      })
      .catch((error) => {
        if (error.code !== "ERR_CANCELED") {
          toast.error("Cardfolio couldn’t search the Set Library.");
        }
      })
      .finally(() => {
        if (requestId === requestSequence.current) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [search, era, productType]);

  return (
    <div className="dashboard-shell workspace-shell" data-testid="set-library-workspace">
      <DashboardHeader user={user} onLogout={onLogout} activePage="Set Library" />
      <main className="workspace-main">
        <header className="workspace-page-header">
          <div>
            <p className="eyebrow">Reference data</p>
            <h1>English Pokémon Set Library</h1>
            <p>
              Search the frozen v1 catalog by official name, partial name, abbreviation, or code.
            </p>
          </div>
          <div className="frozen-library-notice" data-testid="set-library-frozen-notice">
            <ShieldCheck size={18} />
            <span>Working v1 · 154 records</span>
          </div>
        </header>

        <section className="library-filter-panel" data-testid="set-library-filters">
          <label className="library-search-field" htmlFor="set-library-search">
            <Search size={18} aria-hidden="true" />
            <input
              id="set-library-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search Base Set, Scarlet, MEW, 151…"
              data-testid="set-library-search-input"
            />
          </label>
          <label className="workspace-select-field">
            <span>Era</span>
            <select
              value={era}
              onChange={(event) => setEra(event.target.value)}
              data-testid="set-library-era-filter"
            >
              <option value="">All eras</option>
              {facets.eras.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="workspace-select-field">
            <span>Product type</span>
            <select
              value={productType}
              onChange={(event) => setProductType(event.target.value)}
              data-testid="set-library-product-type-filter"
            >
              <option value="">All product types</option>
              {facets.product_types.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className="library-results" data-testid="set-library-results">
          <div className="workspace-section-line">
            <p>{loading ? "Searching frozen v1…" : `${sets.length} matching collections`}</p>
            <p>Full name · partial name · abbreviation/code</p>
          </div>
          <div className="library-table-wrap">
            <table className="library-table" data-testid="set-library-table">
              <thead>
                <tr>
                  <th>Collection</th>
                  <th>Code</th>
                  <th>Era</th>
                  <th>Product type</th>
                </tr>
              </thead>
              <tbody>
                {sets.map((set) => (
                  <tr key={set.set_id} data-testid={`set-library-row-${set.set_id}`}>
                    <td>
                      <strong>{set.display_name || set.set_name}</strong>
                      <span>{set.set_name}</span>
                    </td>
                    <td><code>{set.set_code || "—"}</code></td>
                    <td>{set.era || "Unclassified"}</td>
                    <td>{set.product_type || "Collection"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && sets.length === 0 && (
            <div className="workspace-empty" data-testid="set-library-empty-state">
              <h2>No frozen v1 collection matches that search.</h2>
              <p>Try the official name, a partial title, or a code such as MEW.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}